import notification from "antd/es/notification";
import { groupBy } from "lodash-es";
import { defineMessages } from "react-intl";
import { all, call, put, select, takeEvery, takeLatest } from "redux-saga/effects";

import { EntityType, TripMode, TripStatus } from "@mapmycustomers/shared";
import PlatformFilterOperator from "@mapmycustomers/shared/enum/PlatformFilterOperator";
import type { GeocodeResult } from "@mapmycustomers/shared/types/base/Located";
import { Activity, Company, Person, Trip } from "@mapmycustomers/shared/types/entity";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import { CreateTripPayload, UpdateTripPayload } from "@mapmycustomers/shared/types/entity/Trip";
import Organization from "@mapmycustomers/shared/types/Organization";
import { GeoPoint } from "@mapmycustomers/shared/types/shapes";
import User from "@mapmycustomers/shared/types/User";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import PlatformFilterModel from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";
import getFormattedAddressForUi from "@mapmycustomers/shared/util/formatters/getFormattedAddressForUi";
import pairsGenerator from "@mapmycustomers/shared/util/generator/pairsGenerator";

import i18nService from "@app/config/I18nService";
import { getActivityTypes } from "@app/store/activity";
import { callApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import { getCurrentUserId, getOrganizationId } from "@app/store/iam";
import { notifyAboutChanges } from "@app/store/uiSync/actions";
import { getLocalTimeZone, joinDateAndTime } from "@app/util/dates";
import { formatDate } from "@app/util/formatters";

import IStop from "../types/IStop";

import { createTrips, fetchActivities, updateTrip } from "./actions";

const messages = defineMessages({
  tripsCreated: {
    id: "components.tripModal.notification.success",
    defaultMessage: "{count} {count, plural, one {Drive} other {Drives}} logged successfully",
    description: "A success notification shown after successful trips creation",
  },
});

const EMPTY_RESPONSE: Readonly<ListResponse<any>> = {
  accessible: 0,
  count: 0,
  data: [],
  limit: 0,
  offset: 0,
  total: 0,
};

const disallowedActivityTypes = ["Call", "Email", "Text"];

const getAddressFromStop = (stop: IStop, entity?: Company | Person): null | string =>
  stop.entityType === "address"
    ? getFormattedAddressForUi(stop.address)
    : entity
      ? getFormattedAddressForUi(entity.geoAddress)
      : null;

export function* onFetchActivities({ payload }: ReturnType<typeof fetchActivities>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const currentUserId: User["id"] = yield select(getCurrentUserId);

    let allowedActivityTypes: ActivityType[] = yield select(getActivityTypes);
    allowedActivityTypes = allowedActivityTypes.filter(
      (activityType) =>
        !activityType.isDefault || !disallowedActivityTypes.includes(activityType.name)
    );

    const $filters: PlatformFilterModel = {
      $and: [
        {
          activityTypeId: { $in: allowedActivityTypes.map(({ id }) => id) },
        },
        { assigneeId: currentUserId },
        { completed: true },
        {
          startAt: {
            $timezone: getLocalTimeZone(),
            [PlatformFilterOperator.EQUALS]: formatDate(payload.date, "yyyy-MM-dd"),
          },
        },
        {
          $or: [
            { "account.geoPoint": { [PlatformFilterOperator.NOT_EQUAL]: null } },
            { "contact.geoPoint": { [PlatformFilterOperator.NOT_EQUAL]: null } },
          ],
        },
      ],
    };

    const response: ListResponse<Activity> = yield callApi("fetchActivities", orgId, { $filters });
    if (payload.success) {
      yield call(payload.success, response.data);
    }
  } catch (error) {
    if (payload.failure) {
      yield call(payload.failure);
    }
    yield put(handleError({ error }));
  }
}

type EntityMaps = {
  companiesMap: Map<Company["id"], Company>;
  peopleMap: Map<Person["id"], Person>;
};

function* prepareData(stops: IStop[]) {
  const stopsByEntity = groupBy(stops, "entityType");
  const companyIds = (stopsByEntity[EntityType.COMPANY] ?? []).map(({ entityId }) => entityId!);
  const peopleIds = (stopsByEntity[EntityType.PERSON] ?? []).map(({ entityId }) => entityId!);

  const orgId: Organization["id"] = yield select(getOrganizationId);

  // before we can proceed, we need to fetch companies and people entities because we'll need
  // their coordinates
  const [companiesResponse, peopleResponse]: [ListResponse<Company>, ListResponse<Person>] =
    yield all([
      companyIds.length
        ? callApi("fetchCompanies", orgId, { $filters: { $and: [{ id: { $in: companyIds } }] } })
        : EMPTY_RESPONSE,
      peopleIds.length
        ? callApi("fetchPeople", orgId, { $filters: { $and: [{ id: { $in: peopleIds } }] } })
        : EMPTY_RESPONSE,
    ]);
  const companiesMap = new Map(companiesResponse.data.map((company) => [company.id, company]));
  const peopleMap = new Map(peopleResponse.data.map((person) => [person.id, person]));
  return { companiesMap, peopleMap };
}

type GeoPointAndEntity = [geoPoint: GeoPoint | undefined, entity: Company | Person | undefined];

function* processStop({ companiesMap, peopleMap }: EntityMaps, stop: IStop) {
  let geoPoint: GeoPoint | undefined = undefined;
  let entity: Company | Person | undefined = undefined;

  if (stop.entityType === EntityType.COMPANY && stop.entityId) {
    entity = companiesMap.get(stop.entityId);
    geoPoint = entity?.geoPoint ?? undefined;
  } else if (stop.entityType === EntityType.PERSON && stop.entityId) {
    entity = peopleMap.get(stop.entityId);
    geoPoint = entity?.geoPoint ?? undefined;
  } else if (stop.entityType === "address" && stop.address) {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const result: GeocodeResult = yield callApi("geocodeAddress", orgId, stop.address);
    geoPoint = result?.geoPoint ?? undefined;
  }

  return [geoPoint, entity] as GeoPointAndEntity;
}

function* onCreateTrips({ payload }: ReturnType<typeof createTrips.request>) {
  try {
    const { onSuccess, stops, type } = payload;

    const orgId: Organization["id"] = yield select(getOrganizationId);

    const entityMaps: EntityMaps = yield prepareData(stops);

    const trips: CreateTripPayload[] = [];
    for (const [start, end] of pairsGenerator(stops)) {
      if (!start.departureTime || !end.arrivalTime) {
        continue;
      }

      const [startGeopoint, startEntity]: GeoPointAndEntity = yield processStop(entityMaps, start);
      if (!startGeopoint) {
        continue;
      }

      const [endGeopoint, endEntity]: GeoPointAndEntity = yield processStop(entityMaps, end);
      if (!endGeopoint) {
        continue;
      }

      trips.push({
        endAddress: getAddressFromStop(end, endEntity),
        endEntity: endEntity
          ? { id: endEntity.id, name: endEntity.name, type: endEntity.entity }
          : null,
        endGeopoint,
        endTime: end.arrivalTime.toISOString(),
        mode: TripMode.MANUAL,
        note: null,
        parkingFee: 0,
        startAddress: getAddressFromStop(start, startEntity),
        startEntity: startEntity
          ? { id: startEntity.id, name: startEntity.name, type: startEntity.entity }
          : null,
        startGeopoint,
        startTime: start.departureTime.toISOString(),
        status: TripStatus.COMPLETED,
        tollFee: 0,
        type,
      });
    }

    const createdTrips: Trip[] = yield all(trips.map((trip) => callApi("createTrip", orgId, trip)));

    const notificationText = i18nService.formatMessage(messages.tripsCreated, "Drives logged", {
      count: createdTrips.length,
    });
    notification.success({ message: notificationText });

    yield put(notifyAboutChanges({ added: createdTrips, entityType: EntityType.TRIP }));

    if (onSuccess) {
      yield call(onSuccess, createdTrips);
    }
    yield put(createTrips.success(createdTrips));
  } catch (error) {
    yield put(createTrips.failure());
    yield put(handleError({ error }));
  }
}

function* onUpdateTrip({ payload }: ReturnType<typeof updateTrip.request>) {
  try {
    const { baseDate, onSuccess, originalTrip, stops } = payload;

    const [start, end] = stops;
    if (!start.departureTime || !end.arrivalTime) {
      if (payload.onFailure) {
        yield call(payload.onFailure);
      }
      yield put(updateTrip.failure(new Error("Invalid arrival or departure time")));
      return;
    }

    const entityMaps: EntityMaps = yield prepareData(stops);
    const [startGeopoint, startEntity]: GeoPointAndEntity = yield processStop(entityMaps, start);
    const [endGeopoint, endEntity]: GeoPointAndEntity = yield processStop(entityMaps, end);

    if (!startGeopoint || !endGeopoint) {
      if (payload.onFailure) {
        yield call(payload.onFailure);
      }
      yield put(updateTrip.failure(new Error("Invalid geoPoint")));
      return;
    }

    const tripPayload: UpdateTripPayload = {
      ...originalTrip,
      endAddress: getAddressFromStop(end, endEntity),
      endEntity: endEntity
        ? { id: endEntity.id, name: endEntity.name, type: endEntity.entity }
        : null,
      endGeopoint,
      endTime: joinDateAndTime(baseDate, end.arrivalTime!).toISOString(),
      startAddress: getAddressFromStop(start, startEntity),
      startEntity: startEntity
        ? { id: startEntity.id, name: startEntity.name, type: startEntity.entity }
        : null,
      startGeopoint,
      startTime: joinDateAndTime(baseDate, start.departureTime!).toISOString(),
    };

    const orgId: Organization["id"] = yield select(getOrganizationId);
    const updatedTrip: Trip = yield callApi("updateTrip", orgId, tripPayload);
    if (onSuccess) {
      yield call(onSuccess, updatedTrip);
    }
    yield put(updateTrip.success(updatedTrip));
    yield put(notifyAboutChanges({ entityType: EntityType.TRIP, updated: [updatedTrip] }));
  } catch (error) {
    if (payload.onFailure) {
      yield call(payload.onFailure);
    }
    yield put(updateTrip.failure(error));
    yield put(handleError({ error }));
  }
}

export function* tripModalSaga() {
  yield takeLatest(fetchActivities, onFetchActivities);
  yield takeEvery(createTrips.request, onCreateTrips);
  yield takeEvery(updateTrip.request, onUpdateTrip);
}
